/* Utopia Containers — theme behaviors. Vanilla, no dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* init() runs again on every shopify:section:load in the theme editor, so
     anything that attaches a listener has to be idempotent. Element-level
     bindings are marked with a dataset flag; document- and window-level
     listeners bind once via this guard. */
  var globalsBound = false;

  function claim(el, flag) {
    if (!el || el.dataset[flag]) return false;
    el.dataset[flag] = 'true';
    return true;
  }

  /* ---------------------------------------------------------------- Nav */

  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-nav-drawer]');
    if (!toggle || !drawer) return;
    if (!claim(toggle, 'navBound')) return;

    var close = drawer.querySelector('[data-nav-close]');

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', String(open));
      drawer.toggleAttribute('hidden', !open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        var first = drawer.querySelector('a, button');
        if (first) first.focus();
      } else {
        toggle.focus();
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    if (close) close.addEventListener('click', function () { setOpen(false); });

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setOpen(false);
    });

    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) setOpen(false);
    });
  }

  /* ------------------------------------------------------- Sticky header */

  function initStickyHeader() {
    var header = document.querySelector('[data-sticky-header]');
    if (!header) return;

    var lastKnown = 0;
    var ticking = false;

    function update() {
      header.classList.toggle('is-scrolled', lastKnown > 24);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        lastKnown = window.scrollY;
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* --------------------------------------------------------- Modal (video
     and image lightbox share one implementation: a dialog-ish overlay with a
     focus trap that restores focus to the trigger on close). */

  function trapFocus(container, event) {
    var focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initModals() {
    var openers = document.querySelectorAll('[data-modal-open]');
    if (!openers.length) return;

    openers.forEach(function (opener) {
      var modal = document.getElementById(opener.getAttribute('data-modal-open'));
      if (!modal) return;
      if (!claim(opener, 'modalBound')) return;

      var lastFocused = null;

      function open() {
        lastFocused = document.activeElement;
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';

        // Deferred media: only inject the iframe/video once the user asks for it,
        // so no third-party player script loads on page view.
        var host = modal.querySelector('[data-modal-media]');
        if (host && host.dataset.embed && !host.dataset.loaded) {
          host.innerHTML = host.dataset.embed;
          host.dataset.loaded = 'true';
        }

        var closeBtn = modal.querySelector('[data-modal-close]');
        if (closeBtn) closeBtn.focus();
      }

      function close() {
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';

        // Stop playback by tearing the embed back down.
        var host = modal.querySelector('[data-modal-media]');
        if (host && host.dataset.loaded) {
          host.innerHTML = '';
          delete host.dataset.loaded;
        }
        if (lastFocused) lastFocused.focus();
      }

      opener.addEventListener('click', function (event) {
        event.preventDefault();
        open();
      });

      modal.addEventListener('click', function (event) {
        if (event.target === modal || event.target.closest('[data-modal-close]')) close();
      });

      modal.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
        if (event.key === 'Tab') trapFocus(modal, event);
      });
    });
  }

  /* -------------------------------------------------------- Reveal on scroll */

  function initReveal() {
    var items = document.querySelectorAll('.u-reveal');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------- Announcement dismiss */

  function initAnnouncement() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    var dismiss = bar.querySelector('[data-announcement-dismiss]');
    if (!dismiss) return;
    if (!claim(dismiss, 'announcementBound')) return;

    var key = 'utopia:announcement:' + (bar.dataset.announcement || '1');
    try {
      if (window.sessionStorage.getItem(key) === 'dismissed') {
        bar.setAttribute('hidden', '');
      }
    } catch (e) {
      /* storage unavailable (private mode) — bar simply stays visible */
    }

    dismiss.addEventListener('click', function () {
      bar.setAttribute('hidden', '');
      try {
        window.sessionStorage.setItem(key, 'dismissed');
      } catch (e) {
        /* no-op */
      }
    });
  }

  /* ------------------------------------------------------------ Cart count */

  function initCartCount() {
    document.addEventListener('utopia:cart:updated', function (event) {
      var count = event.detail && event.detail.item_count;
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.toggleAttribute('hidden', !count);
      });
    });
  }

  /* -------------------------------------------------------- Add to cart (AJAX)
     Used by the secondary "add to cart" link on the reservation tiers. The
     primary path stays the native dynamic checkout button. */

  function initAjaxForms() {
    document.querySelectorAll('form[data-ajax-cart]').forEach(function (form) {
      if (!claim(form, 'ajaxBound')) return;

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var button = form.querySelector('[type="submit"]');
        var status = form.querySelector('[data-cart-status]');
        if (button) button.setAttribute('aria-busy', 'true');

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw new Error(data.description || data.message);
              return data;
            });
          })
          .then(function () {
            return fetch(window.Shopify.routes.root + 'cart.js').then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('utopia:cart:updated', { detail: cart }));
            if (status) {
              status.textContent = form.dataset.successText || 'Added to cart';
              status.className = 'u-form-status u-form-status--success';
            }
          })
          .catch(function (error) {
            if (status) {
              status.textContent = error.message;
              status.className = 'u-form-status u-form-status--error';
            }
          })
          .finally(function () {
            if (button) button.removeAttribute('aria-busy');
          });
      });
    });
  }

  /* --------------------------------------------------- Variant price sync
     Several products carry a Deposit variant and a Sale Price variant, so the
     price and the add-to-cart button have to follow the selection. */

  function initVariantSync() {
    document.querySelectorAll('[data-product-root]').forEach(function (root) {
      var select = root.querySelector('[data-variant-select]');
      var priceHost = root.querySelector('[data-variant-price]');
      var button = root.querySelector('[data-variant-button]');
      var json = root.querySelector('[data-variant-json]');
      if (!select || !json) return;
      if (!claim(select, 'variantBound')) return;

      var variants;
      try {
        variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }

      select.addEventListener('change', function () {
        var id = parseInt(select.value, 10);
        var variant = variants.filter(function (v) { return v.id === id; })[0];
        if (!variant) return;

        if (priceHost) {
          var compare = variant.compare_at_price
            ? '<s class="u-price__compare">' + variant.compare_at_price + '</s>'
            : '';
          priceHost.innerHTML =
            '<div class="u-price product__price">' +
            '<span class="u-price__current">' + variant.price + '</span>' +
            compare +
            '</div>';
        }

        if (button) {
          button.disabled = !variant.available;
          button.textContent = variant.available
            ? root.dataset.addLabel
            : root.dataset.soldOutLabel;
        }

        // Keep the URL shareable and the back button honest — but only on a
        // product page. A featured-product section on the home page must not
        // rewrite the home URL with a ?variant= parameter.
        if (root.hasAttribute("data-variant-url-sync") && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      });
    });
  }

  /* ------------------------------------------------------ Product gallery
     Thumbnails swap the main photo; the main photo opens a full-screen viewer
     with previous/next, arrow keys and swipe. The viewer and the page stay in
     step, so closing it leaves the main photo on whichever one was viewed. */

  function initProductGalleries() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (!claim(gallery, 'galleryBound')) return;

      var json = gallery.querySelector('[data-gallery-json]');
      var lightbox = gallery.querySelector('[data-gallery-lightbox]');
      if (!json || !lightbox) return;

      var items;
      try {
        items = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      if (!items.length) return;

      var opener = gallery.querySelector('[data-gallery-open]');
      var mainImg = opener && opener.querySelector('img');
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
      var image = lightbox.querySelector('[data-gallery-image]');
      var figure = lightbox.querySelector('[data-gallery-figure]');
      var closeBtn = lightbox.querySelector('[data-gallery-close]');
      var prevBtn = lightbox.querySelector('[data-gallery-prev]');
      var nextBtn = lightbox.querySelector('[data-gallery-next]');
      var count = lightbox.querySelector('[data-gallery-count]');
      var countTemplate = lightbox.dataset.countTemplate || '[current] / [total]';
      var current = 0;
      var lastFocused = null;

      function show(index) {
        current = (index + items.length) % items.length;
        var item = items[current];
        if (mainImg) {
          mainImg.srcset = item.srcset;
          mainImg.src = item.src;
          mainImg.alt = item.alt;
          mainImg.width = item.width;
          mainImg.height = item.height;
          // The wrapper reserves the photo's aspect ratio to avoid layout shift.
          if (item.ratio && mainImg.parentElement) {
            mainImg.parentElement.style.aspectRatio = String(item.ratio);
          }
        }
        thumbs.forEach(function (thumb, k) {
          thumb.classList.toggle('is-active', k === current);
          if (k === current) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
      }

      function paint() {
        var item = items[current];
        image.src = item.full;
        image.alt = item.alt;
        image.width = item.width;
        image.height = item.height;
        if (count) {
          count.textContent = countTemplate
            .replace('[current]', current + 1)
            .replace('[total]', items.length);
        }
        // Warm the neighbours so stepping through feels instant.
        if (items.length > 1) {
          [current + 1, current - 1].forEach(function (k) {
            new Image().src = items[(k + items.length) % items.length].full;
          });
        }
      }

      function go(step) {
        if (items.length < 2) return;
        show(current + step);
        paint();
      }

      function open() {
        lastFocused = document.activeElement;
        paint();
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
        if (closeBtn) closeBtn.focus();
      }

      function close() {
        lightbox.hidden = true;
        document.body.style.overflow = '';
        if (lastFocused) lastFocused.focus();
      }

      thumbs.forEach(function (thumb, k) {
        thumb.addEventListener('click', function (event) {
          event.preventDefault();
          show(k);
        });
      });

      if (opener) opener.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { go(1); });

      // A click on the dark backdrop closes; a click on the photo doesn't.
      lightbox.addEventListener('click', function (event) {
        if (event.target === lightbox || event.target.classList.contains('gallery-lightbox__dialog') || event.target === figure) {
          close();
        }
      });

      lightbox.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
        else if (event.key === 'ArrowLeft') go(-1);
        else if (event.key === 'ArrowRight') go(1);
        else if (event.key === 'Tab') trapFocus(lightbox, event);
      });

      // Horizontal swipe on touch screens. Mostly-vertical drags are ignored so
      // they don't fight scrolling or pinch-zoom.
      var startX = null;
      var startY = null;
      figure.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) { startX = null; return; }
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      figure.addEventListener('touchend', function (event) {
        if (startX === null) return;
        var dx = event.changedTouches[0].clientX - startX;
        var dy = event.changedTouches[0].clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------ Boot */

  function init() {
    // Shopify.routes is present on the storefront; guard for safety.
    window.Shopify = window.Shopify || {};
    window.Shopify.routes = window.Shopify.routes || { root: '/' };

    // Window/document listeners must only ever be attached once.
    if (!globalsBound) {
      globalsBound = true;
      initStickyHeader();
      initCartCount();
    }

    initNav();
    initModals();
    initReveal();
    initAnnouncement();
    initAjaxForms();
    initVariantSync();
    initProductGalleries();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
